/* ==========================================================================
   CloudDream — product detail page

   Drives the artboard's interactive parts:
     · the desktop gallery (thumbnails, arrows, counter) and the mobile
       snapping carousel's dots — one markup, two behaviours
     · option pickers, which resolve to a real variant and restate price,
       MRP, discount and the CTA total
     · the add-on checkbox, which adds a second line item
     · add to cart, posted as an items[] array so the pillow and its case go
       in together, then handed to Dawn's own cart notification to render
     · save, share, and the offer countdown

   Vanilla, no dependencies — same as the rest of the theme.
   ========================================================================== */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Money

     Shopify's own formatter lives in the storefront's global scope only on
     some themes, so the standard implementation is inlined here. It reads the
     shop's money format off the root element, which is where Liquid put it.
     ---------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(number, size) {
      var n = number == null ? 0 : number;
      return new Array(Math.max(size - String(n).length + 1, 0)).join('0') + n;
    }

    function withDelimiters(number, precision, thousands, decimal) {
      var p = precision == null ? 2 : precision;
      var t = thousands == null ? ',' : thousands;
      var d = decimal == null ? '.' : decimal;

      if (isNaN(number) || number == null) return '0';

      var fixed = (number / 100.0).toFixed(p);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
      var cents_ = parts[1] ? d + parts[1] : '';

      return dollars + cents_;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* Match Liquid's money_without_trailing_zeros. Liquid has already
     normalised the BDT symbol in the money-format data attribute. */
  function money(cents, format) {
    var out = formatMoney(cents, format);
    return out.replace(/([.,])00(?!\d)/, '');
  }

  /* ----------------------------------------------------------------------
     PDP
     ---------------------------------------------------------------------- */

  function initPdp(root) {
    if (root.dataset.cdPdpReady === 'true') return;
    root.dataset.cdPdpReady = 'true';

    var moneyFormat = root.dataset.cdMoneyFormat || '${{amount}}';

    /* ---------------- Gallery ---------------- */

    var frames = Array.prototype.slice.call(root.querySelectorAll('[data-cd-frame]'));
    var variantFrames = Array.prototype.slice.call(root.querySelectorAll('[data-cd-variant-frame]'));
    var variantLayer = root.querySelector('[data-cd-variant-media]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-cd-thumb]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.cd-pdp__dot'));
    var dotContainer = root.querySelector('[data-cd-dots]');
    var counter = root.querySelector('[data-cd-counter]');
    var scroller = root.querySelector('[data-cd-frames]');
    var mobileGalleryQuery = window.matchMedia('(max-width: 989px)');
    var index = Math.max(
      frames.findIndex(function (frame) {
        return frame.classList.contains('is-active');
      }),
      0
    );

    function pad(n) {
      return String(n).padStart(2, '0');
    }

    function positionInitialGalleryMedia() {
      if (!scroller || !frames[index] || !mobileGalleryQuery.matches) return;
      scroller.scrollLeft = frames[index].offsetLeft;
    }

    function hideVariantMedia() {
      variantFrames.forEach(function (frame) {
        frame.classList.remove('is-active');
      });
      if (frames[index]) frames[index].classList.add('is-active');
      if (variantLayer) {
        variantLayer.classList.remove('is-active');
        variantLayer.setAttribute('aria-hidden', 'true');
      }
      if (dotContainer) dotContainer.hidden = false;
      if (counter) counter.hidden = false;
    }

    function paint(i) {
      index = i;
      hideVariantMedia();
      frames.forEach(function (f, n) {
        f.classList.toggle('is-active', n === i);
      });
      thumbs.forEach(function (t, n) {
        t.classList.toggle('is-active', n === i);
        t.setAttribute('aria-selected', n === i ? 'true' : 'false');
        /* Roving tabindex: only the selected tab sits in the page Tab order,
           per the WAI-ARIA tabs pattern. Arrow/Home/End move focus within it. */
        t.setAttribute('tabindex', n === i ? '0' : '-1');
      });
      dots.forEach(function (d, n) {
        d.classList.toggle('is-active', n === i);
      });
      if (counter) counter.textContent = pad(i + 1) + ' / ' + frames.length;
    }

    function paintVariantMedia(activeVariantFrame) {
      frames.forEach(function (frame) {
        frame.classList.remove('is-active');
      });
      variantFrames.forEach(function (frame) {
        frame.classList.toggle('is-active', frame === activeVariantFrame);
      });
      thumbs.forEach(function (thumb) {
        thumb.classList.remove('is-active');
        thumb.setAttribute('aria-selected', 'false');
      });
      dots.forEach(function (dot) {
        dot.classList.remove('is-active');
      });
      if (variantLayer) {
        variantLayer.classList.add('is-active');
        variantLayer.setAttribute('aria-hidden', 'false');
      }
      if (dotContainer) dotContainer.hidden = true;
      if (counter) counter.hidden = true;
    }

    function selectVariantMedia(v) {
      hideVariantMedia();
      if (!v || !v.featured_media_id || !variantFrames.length) return;

      var activeVariantFrame = variantFrames.find(function (frame) {
        return String(frame.dataset.cdMediaId) === String(v.featured_media_id);
      });
      if (!activeVariantFrame) return;

      paintVariantMedia(activeVariantFrame);
    }

    /* Prime the roving tabindex from the server-rendered active thumb, before
       any interaction calls paint() itself. */
    thumbs.forEach(function (t, n) {
      t.setAttribute('tabindex', n === index ? '0' : '-1');
    });

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        paint(parseInt(t.dataset.cdThumb, 10) || 0);
      });
      t.addEventListener('keydown', function (event) {
        var last = thumbs.length - 1;
        var target = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          target = index === last ? 0 : index + 1;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          target = index === 0 ? last : index - 1;
        } else if (event.key === 'Home') {
          target = 0;
        } else if (event.key === 'End') {
          target = last;
        } else {
          return;
        }
        event.preventDefault();
        paint(target);
        thumbs[target].focus();
      });
    });

    var prev = root.querySelector('[data-cd-prev]');
    var next = root.querySelector('[data-cd-next]');
    if (prev) {
      prev.addEventListener('click', function () {
        paint((index + frames.length - 1) % frames.length);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        paint((index + 1) % frames.length);
      });
    }

    /* On mobile every frame is laid out side by side and the customer scrolls,
       so the dots follow the scroller rather than the click. */
    if (scroller && frames.length > 1) {
      /* Deliberately not gated behind requestAnimationFrame. A scroll that
         happens while the tab is hidden never gets its frame callback, which
         would leave a "waiting for rAF" latch stuck on and kill every later
         update. The work here is two reads and a class toggle, and scroll
         events are already coalesced, so it runs inline. */
      scroller.addEventListener(
        'scroll',
        function () {
          hideVariantMedia();
          var w = scroller.clientWidth || 1;
          var i = Math.round(scroller.scrollLeft / w);
          if (i !== index && frames[i]) {
            index = i;
            dots.forEach(function (d, n) {
              d.classList.toggle('is-active', n === i);
            });
          }
        },
        { passive: true }
      );
    }

    /* ---------------- Variants ---------------- */

    var variantData = null;
    var dataEl = document.querySelector(
      '[data-cd-variants="' + window.CSS.escape(root.dataset.cdSectionId || '') + '"]'
    );
    if (dataEl) {
      try {
        variantData = JSON.parse(dataEl.textContent);
      } catch (e) {
        variantData = null;
      }
    }

    var idInput = root.querySelector('[data-cd-variant-id]');
    var priceEl = root.querySelector('[data-cd-price]');
    var compareEl = root.querySelector('[data-cd-compare]');
    var offEl = root.querySelector('[data-cd-off]');
    var atc = root.querySelector('[data-cd-atc]');
    var atcTotal = root.querySelector('[data-cd-cta-total]');
    var atcLabel = root.querySelector('[data-cd-atc-label]');
    var atcSeparator = root.querySelector('[data-cd-atc-separator]');
    var stickyAtc = root.querySelector('[data-cd-sticky-atc]');
    var addonInput = root.querySelector('[data-cd-addon]');
    var quantityInput = root.querySelector('[data-cd-quantity]');
    var comboRoot = root.querySelector('[data-cd-combo]');
    var comboInputs = Array.prototype.slice.call(root.querySelectorAll('[data-cd-combo-input]'));
    var comboCards = Array.prototype.slice.call(root.querySelectorAll('[data-cd-combo-card]'));
    var comboSelectionAvailable = true;
    var stock = root.querySelector('[data-cd-stock]');
    var stockText = root.querySelector('[data-cd-stock-text]');
    var skuWrap = root.querySelector('[data-cd-sku-wrap]');
    var sku = root.querySelector('[data-cd-sku]');
    var optionGroups = Array.prototype.slice.call(root.querySelectorAll('[data-cd-option]'));

    /* ---------------- Sticky mobile purchase shortcut ---------------- */

    if (atc && stickyAtc) {
      var mobileStickyQuery = window.matchMedia('(max-width: 989px)');
      var atcIsVisible = true;

      function currentPurchaseButton() {
        var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-cd-atc]'));
        return (
          buttons.find(function (button) {
            var rect = button.getBoundingClientRect();
            var style = window.getComputedStyle(button);
            return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
          }) || atc
        );
      }

      function syncStickyAtcState() {
        var purchaseButton = currentPurchaseButton();
        var disabled = purchaseButton.disabled || purchaseButton.getAttribute('aria-disabled') === 'true';
        var busy = purchaseButton.getAttribute('aria-busy') === 'true';

        stickyAtc.disabled = disabled;
        stickyAtc.setAttribute('aria-disabled', String(disabled));
        if (busy) {
          stickyAtc.setAttribute('aria-busy', 'true');
        } else {
          stickyAtc.removeAttribute('aria-busy');
        }
      }

      function updateStickyAtc() {
        var show = mobileStickyQuery.matches && !atcIsVisible;
        stickyAtc.classList.toggle('is-visible', show);
        stickyAtc.setAttribute('aria-hidden', String(!show));
        stickyAtc.tabIndex = show ? 0 : -1;
        syncStickyAtcState();
      }

      var observedAtc = null;
      var atcObserver = new IntersectionObserver(function (entries) {
        atcIsVisible = !!entries[0]?.isIntersecting;
        updateStickyAtc();
      });

      function observePurchaseButton() {
        var purchaseButton = currentPurchaseButton();
        if (purchaseButton === observedAtc) return;
        if (observedAtc) atcObserver.unobserve(observedAtc);
        observedAtc = purchaseButton;
        atcObserver.observe(purchaseButton);
      }

      function containsPurchaseButton(node) {
        return (
          node.nodeType === 1 &&
          (node.matches('[data-cd-atc]') || node.querySelector('[data-cd-atc]'))
        );
      }

      var atcStateObserver = new MutationObserver(function (mutations) {
        var purchaseButtonChanged = mutations.some(function (mutation) {
          if (mutation.type === 'attributes') return mutation.target.matches('[data-cd-atc]');
          return (
            Array.prototype.some.call(mutation.addedNodes, containsPurchaseButton) ||
            Array.prototype.some.call(mutation.removedNodes, containsPurchaseButton)
          );
        });
        if (!purchaseButtonChanged) return;
        observePurchaseButton();
        syncStickyAtcState();
      });
      atcStateObserver.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'disabled', 'aria-disabled', 'aria-busy']
      });

      observePurchaseButton();
      syncStickyAtcState();

      if (typeof mobileStickyQuery.addEventListener === 'function') {
        mobileStickyQuery.addEventListener('change', updateStickyAtc);
      } else {
        mobileStickyQuery.addListener(updateStickyAtc);
      }

      stickyAtc.addEventListener('click', function () {
        var purchaseButton = currentPurchaseButton();
        purchaseButton.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });
        window.setTimeout(function () {
          currentPurchaseButton().focus({ preventScroll: true });
        }, 450);
      });
    }

    function selectedOptions() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-cd-option]')).map(function (group) {
        var checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function currentVariant() {
      if (!variantData) return null;
      var chosen = selectedOptions();
      if (!chosen.length) return null;
      for (var i = 0; i < variantData.length; i++) {
        var v = variantData[i];
        var match = true;
        for (var j = 0; j < chosen.length; j++) {
          if (chosen[j] !== null && v.options[j] !== chosen[j]) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    function addonCents() {
      if (!addonInput || !addonInput.checked) return 0;
      return parseInt(addonInput.dataset.cdAddonPrice, 10) || 0;
    }

    function quantity() {
      if (!quantityInput) return 1;
      var value = parseInt(quantityInput.value, 10);
      var min = parseInt(quantityInput.min, 10) || 1;
      var max = quantityInput.max ? parseInt(quantityInput.max, 10) : null;
      var step = parseInt(quantityInput.step, 10) || 1;

      if (!Number.isFinite(value) || value < min) value = min;
      if (max !== null && Number.isFinite(max) && value > max) value = max;
      var remainder = (value - min) % step;
      if (remainder !== 0) value -= remainder;
      quantityInput.value = String(value);
      return value;
    }

    function basePrice() {
      var v = currentVariant();
      if (v) return v.price;
      return parseInt(root.dataset.cdBasePrice, 10) || 0;
    }

    function configuredComboTotal(price, card) {
      if (!card) return price * quantity();
      var comboQuantity = parseInt(card.dataset.cdComboQty, 10) || 1;
      var tier = parseInt(card.dataset.cdComboTier, 10) || 1;
      var pricingTable = comboRoot?.dataset.cdComboPricing || '';
      var unitPriceTaka = Math.round(price / 100);
      var rows = pricingTable.split(/[;\n\r]+/);

      for (var rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        var fields = rows[rowIndex].trim().split(':').map(function (value) {
          return parseInt(value.trim(), 10) || 0;
        });
        if (fields[0] === unitPriceTaka && fields[tier] > 0) return fields[tier] * 100;
      }
      return price * comboQuantity;
    }

    function selectedComboCard() {
      return comboCards.find(function (card) {
        var input = card.querySelector('[data-cd-combo-input]');
        return input && input.checked && !input.disabled;
      });
    }

    function refreshTotal() {
      if (atcTotal) {
        atcTotal.textContent = money(configuredComboTotal(basePrice(), selectedComboCard()) + addonCents(), moneyFormat);
      }
    }

    function syncComboSelection() {
      if (!quantityInput || !comboInputs.length) return;
      var checked = comboInputs.find(function (input) {
        return input.checked && !input.disabled;
      });
      comboCards.forEach(function (card) {
        var input = card.querySelector('[data-cd-combo-input]');
        card.classList.toggle('is-selected', !!(input && input.checked && !input.disabled));
      });
      if (!checked) return;

      quantityInput.value = checked.value;
    }

    /* The lowest ceiling that applies: a merchant quantity rule, or the stock we
       are actually allowed to sell. Null from either means "no ceiling". */
    function comboCeiling(v, rule) {
      var ruleMax = rule.max == null ? null : parseInt(rule.max, 10);
      var stock = v && v.inventory_quantity;
      stock = typeof stock === 'number' && Number.isFinite(stock) ? stock : null;
      if (ruleMax === null) return stock;
      if (stock === null) return ruleMax;
      return Math.min(ruleMax, stock);
    }

    function updateComboAvailability(v) {
      if (!comboInputs.length || !quantityInput) return;

      var rule = (v && v.quantity_rule) || {};
      var min = parseInt(rule.min, 10) || parseInt(quantityInput.min, 10) || 1;
      // Offering a 3-pack of a variant with two left sent an over-stock add that
      // Shopify trimmed to two and reported as an error, so the pack tiers stop
      // at whatever can actually ship.
      var max = comboCeiling(v, rule);
      var increment = parseInt(rule.increment, 10) || parseInt(quantityInput.step, 10) || 1;

      comboInputs.forEach(function (input) {
        var value = parseInt(input.value, 10);
        var valid = value >= min && (max === null || value <= max) && (value - min) % increment === 0;
        input.disabled = !valid;
        input.setAttribute('aria-disabled', String(!valid));
        input.closest('[data-cd-combo-card]')?.classList.toggle('is-unavailable', !valid);
      });

      var checked = comboInputs.find(function (input) {
        return input.checked && !input.disabled;
      });
      if (!checked) {
        var fallback = comboInputs.find(function (input) {
          return !input.disabled;
        });
        if (fallback) {
          fallback.checked = true;
        } else {
          comboInputs.forEach(function (input) {
            input.checked = false;
          });
          quantityInput.value = String(min);
        }
      }
      comboSelectionAvailable = comboInputs.some(function (input) {
        return !input.disabled;
      });
      if (comboRoot) comboRoot.setAttribute('aria-invalid', String(!comboSelectionAvailable));
      syncComboSelection();
    }

    function updateComboPrices(v) {
      if (!v || !comboCards.length) return;
      var saveLabel = comboRoot?.dataset.cdComboSaveLabel || 'Save';
      var badgeKey = comboRoot?.dataset.cdComboBadgeKey || 'Save';

      comboCards.forEach(function (card) {
        var comboQuantity = parseInt(card.dataset.cdComboQty, 10) || 1;
        var mrpUnit = v.compare_at_price && v.compare_at_price > 0 ? v.compare_at_price : v.price;
        var compareTotal = mrpUnit * comboQuantity;
        var total = configuredComboTotal(v.price, card);
        var saving = Math.max(compareTotal - total, 0);
        var extraSaving = Math.max(v.price * comboQuantity - total, 0);
        var price = card.querySelector('[data-cd-combo-price]');
        var compare = card.querySelector('[data-cd-combo-compare]');
        var compareValue = card.querySelector('[data-cd-combo-compare-value]');
        var unit = card.querySelector('[data-cd-combo-unit-price]');
        var save = card.querySelector('[data-cd-combo-save]');
        var badge = card.querySelector('[data-cd-combo-badge]');
        var badgeValue = card.querySelector('[data-cd-combo-badge-value]');

        if (price) price.textContent = money(total, moneyFormat);
        if (unit) unit.textContent = money(Math.round(total / comboQuantity / 100) * 100, moneyFormat);
        if (compare) compare.hidden = false;
        if (compareValue) compareValue.textContent = money(compareTotal, moneyFormat);
        if (save) {
          save.hidden = saving <= 0;
          save.textContent = saveLabel + ' ' + money(saving, moneyFormat);
        }
        if (badge) badge.hidden = extraSaving <= 0;
        if (badgeValue) badgeValue.textContent = money(extraSaving, moneyFormat);
        var badgeKeyNode = badge?.querySelector('.cd-pdp__combo-chip-key');
        if (badgeKeyNode) badgeKeyNode.textContent = badgeKey;
      });
    }

    function updateOptionAvailability() {
      if (!variantData || !optionGroups.length) return;

      var selected = selectedOptions();
      optionGroups.forEach(function (group, groupIndex) {
        var inputs = Array.prototype.slice.call(group.querySelectorAll('[data-cd-opt-input]'));

        inputs.forEach(function (input) {
          var available = variantData.some(function (candidate) {
            if (!candidate.available || candidate.options[groupIndex] !== input.value) return false;
            for (var previous = 0; previous < groupIndex; previous++) {
              if (selected[previous] && candidate.options[previous] !== selected[previous]) return false;
            }
            return true;
          });

          input.disabled = !available;
          input.setAttribute('aria-disabled', String(!available));
          input.closest('.cd-pdp__opt')?.classList.toggle('is-unavailable', !available);
        });

        var checked = group.querySelector('input:checked');
        if (checked && checked.disabled) {
          var fallback = inputs.find(function (input) {
            return !input.disabled;
          });
          if (fallback) {
            fallback.checked = true;
            selected[groupIndex] = fallback.value;
          }
        } else if (checked) {
          selected[groupIndex] = checked.value;
        }
      });
    }

    function updateQuantityRules(v) {
      if (!quantityInput || !v || !v.quantity_rule) return;

      var rule = v.quantity_rule;
      var min = parseInt(rule.min, 10) || 1;
      var increment = parseInt(rule.increment, 10) || 1;
      var max = comboCeiling(v, rule);
      var value = parseInt(quantityInput.value, 10) || min;

      quantityInput.min = String(min);
      quantityInput.dataset.min = String(min);
      quantityInput.step = String(increment);
      if (max !== null && Number.isFinite(max)) {
        quantityInput.max = String(max);
        quantityInput.dataset.max = String(max);
      } else {
        quantityInput.removeAttribute('max');
        delete quantityInput.dataset.max;
      }

      if (value < min) value = min;
      if (max !== null && Number.isFinite(max) && value > max) value = max;
      var remainder = (value - min) % increment;
      if (remainder !== 0) value -= remainder;
      quantityInput.value = String(value);

      var quantityElement = quantityInput.closest('quantity-input');
      if (quantityElement && typeof quantityElement.validateQtyRules === 'function') {
        quantityElement.validateQtyRules();
      }
      updateComboAvailability(v);
    }

    function updatePurchaseState(v) {
      var productAvailable = !!(v && v.available);
      var available = productAvailable && comboSelectionAvailable;
      var label = !v || (productAvailable && !comboSelectionAvailable)
        ? atc?.dataset.cdUnavailable || 'Unavailable'
        : productAvailable
          ? atc?.dataset.cdLabel || 'Add to cart'
          : atc?.dataset.cdSoldOut || 'Sold out';

      if (atcLabel) atcLabel.textContent = label;
      if (atcSeparator) atcSeparator.hidden = !available;
      if (atcTotal) atcTotal.hidden = !available;
      if (atc) {
        atc.disabled = !available;
        atc.setAttribute('aria-disabled', String(!available));
      }
      if (stickyAtc) {
        stickyAtc.disabled = !available;
        stickyAtc.setAttribute('aria-disabled', String(!available));
      }

      if (stock && stockText && v) {
        stock.classList.toggle('is-out', !productAvailable);
        stockText.textContent = productAvailable ? root.dataset.cdInStock : root.dataset.cdSoldOut;
      }

      if (skuWrap && sku && v) {
        sku.textContent = v.sku || '';
        skuWrap.classList.toggle('is-hidden', !v.sku);
      }
    }

    function applyVariant(shouldSelectMedia) {
      /* A product with only a default variant has no pickers and nothing to
         resolve — leave the server-rendered price and button exactly as they
         are rather than falling through to the "no match" branch below. */
      if (!variantData || !optionGroups.length) {
        var defaultVariant = null;
        if (variantData && idInput) {
          defaultVariant = variantData.find(function (item) {
            return String(item.id) === String(idInput.value);
          });
        }
        if (defaultVariant) {
          updateQuantityRules(defaultVariant);
          updateComboPrices(defaultVariant);
          updatePurchaseState(defaultVariant);
        }
        if (atc && !defaultVariant) {
          var defaultAvailable = !(idInput && idInput.disabled);
          atc.disabled = !defaultAvailable;
          atc.setAttribute('aria-disabled', String(!defaultAvailable));
        }
        refreshTotal();
        return;
      }

      updateOptionAvailability();
      var v = currentVariant();

      /* Restate the picked value beside each option's name. */
      Array.prototype.slice.call(root.querySelectorAll('[data-cd-option]')).forEach(function (group) {
        var checked = group.querySelector('input:checked');
        var label = group.querySelector('[data-cd-option-value]');
        if (checked && label) label.textContent = checked.value;
        Array.prototype.slice.call(group.querySelectorAll('.cd-pdp__opt')).forEach(function (opt) {
          var input = opt.querySelector('input');
          opt.classList.toggle('is-selected', !!(input && input.checked));
        });
      });

      /* An option combination that maps to no variant: disable, but write to
         the label span so the spinner and total markup survive. */
      if (!v) {
        updatePurchaseState(null);
        if (shouldSelectMedia !== false) hideVariantMedia();
        return;
      }

      if (idInput) {
        idInput.value = v.id;
        idInput.disabled = !v.available;
      }
      if (priceEl) priceEl.textContent = money(v.price, moneyFormat);
      updateQuantityRules(v);
      updateComboPrices(v);

      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.hidden = false;
          var s = compareEl.querySelector('s');
          if (s) s.textContent = money(v.compare_at_price, moneyFormat);
          if (offEl) {
            offEl.hidden = false;
            offEl.textContent = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) + '% OFF';
          }
        } else {
          compareEl.hidden = true;
          if (offEl) offEl.hidden = true;
        }
      }

      updatePurchaseState(v);
      refreshTotal();
      if (shouldSelectMedia !== false) selectVariantMedia(v);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /* Use click rather than change so clicking an already-selected swatch still
       reveals its variant image. Keyboard activation of a radio also fires click. */
    root.addEventListener('click', function (evt) {
      if (evt.target.matches('[data-cd-opt-input]')) applyVariant();
    });

    root.addEventListener('change', function (evt) {
      if (evt.target.matches('[data-cd-addon]')) refreshTotal();
      if (evt.target.matches('[data-cd-quantity]')) refreshTotal();
      if (evt.target.matches('[data-cd-combo-input]')) {
        syncComboSelection();
        refreshTotal();
      }
    });

    /* ---------------- Add to cart ---------------- */

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var form = root.querySelector('.cd-pdp__form');
    var errorEl = root.querySelector('[data-cd-error]');
    var spinner = root.querySelector('.cd-pdp__spinner');

    function sectionsPayload() {
      if (hasExternalCartDrawer()) return null;
      if (!cart || typeof cart.getSectionsToRender !== 'function') return null;
      return cart
        .getSectionsToRender()
        .map(function (section) {
          return section.id;
        })
        .join(',');
    }

    function addItems(items, button) {
      var body = { items: items };
      var sections = sectionsPayload();
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      if (button) button.setAttribute('aria-disabled', 'true');
      if (button) button.disabled = true;
      if (button) button.setAttribute('aria-busy', 'true');
      if (spinner && button === atc) spinner.classList.remove('hidden');

      var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      return fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          // A stock-capped add comes back as 422 with the items Shopify *did*
          // take already in the cart ("Only 2 items were added to your cart due
          // to availability"). Treating that as a plain failure left those items
          // sitting in a cart the shopper had just been told was unchanged, which
          // is how the cart appeared to add stock that was not there. Say what
          // happened, then carry on and show them the cart.
          var partial = response.status === 422 && /due to availability/i.test(
            response.description || response.message || '',
          );

          if (response.status && !partial) {
            if (errorEl) {
              errorEl.hidden = false;
              errorEl.textContent = response.description || response.message || '';
            }
            return;
          }

          if (errorEl) {
            errorEl.hidden = !partial;
            errorEl.textContent = partial ? response.description || response.message || '' : '';
          }

          if (!hasExternalCartDrawer() && cart && typeof cart.renderContents === 'function' && response.sections) {
            cart.renderContents(response);
          } else if (!hasExternalCartDrawer()) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = root.dataset.cdAddError || '';
          }
        })
        .finally(function () {
          if (button) button.removeAttribute('aria-busy');
          if (button && button !== atc) {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
          }
          if (spinner && button === atc) spinner.classList.add('hidden');
          if (button === atc) applyVariant(false);
        });
    }

    if (form) {
      form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        if (atc && atc.getAttribute('aria-disabled') === 'true') return;

        var id = idInput ? idInput.value : null;
        if (!id) return;

        var items = [{ id: Number(id), quantity: quantity() }];
        if (addonInput && addonInput.checked) {
          items.push({ id: Number(addonInput.dataset.cdAddonId), quantity: 1 });
        }
        addItems(items, atc);
      });
    }

    /* Cross-sell "ADD" buttons put one covered item in the cart directly. */
    Array.prototype.slice.call(root.querySelectorAll('[data-cd-quick-add]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        addItems([{ id: Number(btn.dataset.cdQuickAdd), quantity: 1 }], btn);
      });
    });

    /* ---------------- Save ---------------- */

    var save = root.querySelector('[data-cd-save]');
    if (save) {
      var key = 'cd-saved-' + (root.dataset.cdProductId || '');
      try {
        if (window.localStorage.getItem(key) === '1') save.setAttribute('aria-pressed', 'true');
      } catch (e) {
        /* Private mode — the button still toggles, it just will not persist. */
      }
      save.addEventListener('click', function () {
        var on = save.getAttribute('aria-pressed') === 'true';
        save.setAttribute('aria-pressed', on ? 'false' : 'true');
        try {
          if (on) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, '1');
          }
        } catch (e) {
          /* no-op */
        }
      });
    }

    /* ---------------- Share ---------------- */

    var share = root.querySelector('[data-cd-share]');
    if (share) {
      share.addEventListener('click', function () {
        var payload = { title: share.dataset.cdShareTitle || document.title, url: window.location.href };
        if (navigator.share) {
          navigator.share(payload).catch(function () {
            /* Customer dismissed the sheet. */
          });
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(window.location.href).then(function () {
            share.classList.add('is-copied');
            window.setTimeout(function () {
              share.classList.remove('is-copied');
            }, 1600);
          });
        }
      });
    }

    /* ---------------- Offer countdown ----------------

       A countdown only runs from a real, fixed timestamp entered by the
       merchant. Invalid or expired deadlines hide themselves. */

    var clock = root.querySelector('[data-cd-countdown]');
    if (clock) {
      var deadline = Date.parse(clock.dataset.cdEndsAt || '');
      var endsLabel = root.querySelector('[data-cd-offer-ends]');
      if (Number.isFinite(deadline)) {
        var timer;
        var tick = function () {
          var left = deadline - Date.now();
          if (left <= 0) {
            clock.hidden = true;
            if (endsLabel) endsLabel.hidden = true;
            if (timer) window.clearInterval(timer);
            return;
          }

          var total = Math.floor(left / 1000);
          clock.textContent =
            pad(Math.floor(total / 3600)) + ':' + pad(Math.floor((total % 3600) / 60)) + ':' + pad(total % 60);
        };
        tick();
        timer = window.setInterval(tick, 1000);
      } else {
        clock.hidden = true;
        if (endsLabel) endsLabel.hidden = true;
      }
    }

    /* ------------------------------------------------------------ size guide
       A native dialog, so Escape and focus trapping come free. It sits
       inside the product form, which rules out form method="dialog" — the
       close button and a click on the backdrop close it here instead. */
    var sizeGuide = root.querySelector('[data-cd-size-guide]');
    if (sizeGuide && !sizeGuide.hasAttribute('data-cd-bound')) {
      sizeGuide.setAttribute('data-cd-bound', '');
      var sizeGuideOpener = null;

      Array.prototype.slice.call(root.querySelectorAll('[data-cd-size-guide-open]')).forEach(function (btn) {
        btn.addEventListener('click', function () {
          sizeGuideOpener = btn;
          if (typeof sizeGuide.showModal === 'function') {
            sizeGuide.showModal();
          } else {
            sizeGuide.setAttribute('open', '');
          }
        });
      });

      var closeSizeGuide = function () {
        if (typeof sizeGuide.close === 'function') {
          sizeGuide.close();
        } else {
          sizeGuide.removeAttribute('open');
        }
      };

      var sizeGuideClose = sizeGuide.querySelector('[data-cd-size-guide-close]');
      if (sizeGuideClose) sizeGuideClose.addEventListener('click', closeSizeGuide);
      sizeGuide.addEventListener('click', function (event) {
        if (event.target === sizeGuide) closeSizeGuide();
      });
      sizeGuide.addEventListener('close', function () {
        if (sizeGuideOpener) sizeGuideOpener.focus({ preventScroll: true });
      });
    }

    applyVariant(false);
    positionInitialGalleryMedia();
    refreshTotal();
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('[data-cd-pdp]')).forEach(initPdp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor re-renders a section on every settings change. */
  document.addEventListener('shopify:section:load', boot);
})();
